import type { Config } from 'tailwindcss';
import Colors from 'tailwindcss/colors';
import Typography from '@tailwindcss/typography';
import Forms from '@tailwindcss/forms';

export const TailwindConfig: Config = {
  content: [],
  darkMode: 'class',
  theme: {
    colors: {
      transparent: 'transparent',
      current: 'currentColor',
      black: '#212529',
      white: '#ffffff',
      slate: Colors.slate,
      green: Colors.green,
      amber: Colors.amber,
      'copernicus-yellow': '#f2b616',
      'zight-orange': '#ff9a17',
      'zight-magenta': '#fe2b67',
      'zight-charm': '#dc7597',
      'zight-champagne': '#fdda9b',
      'zight-lavender': '#d0d1fb',
      'zight-fog': '#c8c9db',
      'zight-eucalyptus': '#75dcba',
      'zight-viridian': '#248a68',
      zinc: {
        50: '#F9FAFB',
        100: '#F3F5F7',
        200: '#E4E8ED',
        300: '#CED6DE',
        400: '#94A6B8',
        500: '#667F99',
        600: '#4F6278',
        700: '#3B4959',
        800: '#242D37',
        900: '#1C232B',
      },
      blue: {
        50: '#F5FAFF',
        100: '#EBF4FF',
        200: '#D1E7FF',
        300: '#ADD5FF',
        400: '#4DA3FF',
        500: '#007AFF',
        600: '#005EC7',
        700: '#004694',
        800: '#002B5C',
        900: '#002247',
      },
      yellow: {
        50: '#FEFBF1',
        100: '#FDF3D8',
        200: '#FAE4A8',
        300: '#F8D478',
        400: '#F5C547',
        500: '#F2B516',
        600: '#CB960B',
        700: '#9B7208',
        800: '#6A4F06',
        900: '#3A2B03',
      },
      orange: {
        50: '#FEFAF6',
        100: '#FDF5ED',
        200: '#F9E9D7',
        300: '#F5D9B7',
        400: '#E9AB62',
        500: '#E0871F',
        600: '#AF6A18',
        700: '#824F12',
        800: '#51310B',
        900: '#3F2609',
      },
      red: {
        50: '#FEF7F6',
        100: '#FDEFED',
        200: '#FADBD6',
        300: '#F6BFB6',
        400: '#EC7360',
        500: '#E4381B',
        600: '#B22B15',
        700: '#842010',
        800: '#52140A',
        900: '#401008',
      },
      magenta: {
        50: '#FFF5F8',
        100: '#FFE0E9',
        200: '#FFB2C8',
        300: '#FF85A8',
        400: '#FF5787',
        500: '#FF2A67',
        600: '#F50046',
        700: '#C20038',
        800: '#8F0029',
        900: '#5C001A',
      },
    },
    fontSize: {
      xs: ['.625rem', { lineHeight: '1.5' }],
      sm: ['.75rem', { lineHeight: '1.5' }],
      base: ['.875rem', { lineHeight: '1.5' }],
      lg: ['.875rem', { lineHeight: '1.3', letterSpacing: '-.02em' }],
      xl: ['1rem', { lineHeight: '1.3', letterSpacing: '-.02em' }],
      '2xl': ['1.125rem', { lineHeight: '1.3', letterSpacing: '-.02em' }],
      '3xl': ['1.25rem', { lineHeight: '1.3', letterSpacing: '-.02em' }],
      '4xl': ['1.5rem', { lineHeight: '1.3', letterSpacing: '-.02em' }],
      '5xl': ['2rem', { lineHeight: '1.3', letterSpacing: '-.02em' }],
      '6xl': ['2.5rem', { lineHeight: '1.3', letterSpacing: '-.02em' }],
    },
    extend: {
      scale: {
        25: '.25',
        98: '.98',
        99: '.99',
        103: '1.03',
      },
      spacing: {
        112: '28rem',
        128: '32rem',
        144: '36rem',
      },
      screens: {
        '3xl': '1800px',
        '4xl': '2100px',
      },
      maxWidth: {
        xxs: '10rem',
        '8xl': '88rem',
        '9xl': '96rem',
      },
      borderRadius: {
        4: '0.25rem',
        10: '0.625rem',
      },
    },
  },
  plugins: [
    Typography, 
    Forms,
    // Custom utility plugin to add hidden-scrollbar class
    // This resolves PostCSS errors when using @apply with custom classes in Vue components
    function({ addUtilities }) {
      addUtilities({
        '.hidden-scrollbar': {
          /* Hide scrollbar for Chrome, Safari and Opera */
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          /* Hide scrollbar for IE, Edge and Firefox */
          '-ms-overflow-style': 'none', /* IE and Edge */
          'scrollbar-width': 'none', /* Firefox */
        },
      })
    }
  ],
};
